import { useCallback, useRef, useState } from "react";
import { exportFormSubmissions } from "@/api/forms";
import { createExcelSyncPackage } from "@/api/submissionSync";
import { ApiError } from "@/api/client";
import { IconChevronDown, IconDownload } from "@/components/icons";
import { useClickOutside } from "@/hooks/useClickOutside";
import { unauthorizedMessage } from "@/lib/auth/unauthorizedMessage";
import { resolveExportFieldParams } from "@/lib/forms/submissionsExport";
import { buildExcelSyncPackagePayload } from "@/lib/submissionSync/query";
import {
  downloadSubmissionSyncSidecar,
  sidecarFilenameFromExcel,
} from "@/lib/submissionSync/sidecar";
import type { SubmissionsExportMetaKey } from "@/types/submission";

export function SubmissionsExcelSync({
  formId,
  catalogFieldIds,
  orderedFieldIds,
  selectedFieldIds,
  selectedMetaKeys,
  staffColumnIds,
  selectedStaffColumnIds,
  allowStaffMarks,
  onError,
}: {
  formId: number;
  catalogFieldIds: number[];
  orderedFieldIds: number[];
  selectedFieldIds: Set<number>;
  selectedMetaKeys: SubmissionsExportMetaKey[];
  staffColumnIds: number[];
  selectedStaffColumnIds: Set<number>;
  allowStaffMarks: boolean;
  onError: (message: string | null) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  useClickOutside(containerRef, close, open);

  const [includeStaffMarks, setIncludeStaffMarks] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleDownload = async () => {
    onError(null);
    setBusy(true);
    try {
      const { fieldIds, order } = resolveExportFieldParams(
        catalogFieldIds,
        orderedFieldIds,
        selectedFieldIds,
      );
      const withMarks = allowStaffMarks && includeStaffMarks;
      const meta = selectedMetaKeys.length > 0 ? selectedMetaKeys : undefined;
      const packagePayload = buildExcelSyncPackagePayload({
        fieldIds,
        meta,
        order,
        includeStaffMarks: withMarks,
        staffColumnIds,
        selectedStaffColumnIds,
      });

      const { sidecar } = await createExcelSyncPackage(formId, packagePayload);

      let excelName = "submissions.xlsx";
      try {
        excelName = await exportFormSubmissions(formId, {
          format: "xlsx",
          metaKeys: meta,
          fieldIds,
          order,
          staffColumns:
            packagePayload.staffColumns &&
            packagePayload.staffColumns.length > 0
              ? packagePayload.staffColumns
              : undefined,
        });
      } catch (err) {
        onError(
          err instanceof ApiError
            ? `Excel скачать не удалось: ${err.message}. Sidecar всё равно сохранится.`
            : "Excel скачать не удалось. Sidecar всё равно сохранится.",
        );
      }

      downloadSubmissionSyncSidecar(
        sidecar,
        sidecarFilenameFromExcel(excelName),
      );
      close();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 401) onError(unauthorizedMessage());
        else if (err.status === 403) {
          onError("Нет доступа к Excel-синку. Нужен просмотр ответов.");
        } else {
          onError(
            err.message || `Не удалось собрать пакет Excel (${err.status})`,
          );
        }
      } else {
        onError("Не удалось собрать пакет Excel");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((current) => !current)}
        className="inline-flex h-8 items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 font-onest text-xs font-medium text-slate-700 shadow-sm hover:border-slate-300 hover:bg-slate-50"
      >
        Excel
        <IconChevronDown
          size={14}
          className={[
            "text-slate-400 transition-transform",
            open ? "rotate-180" : "",
          ].join(" ")}
        />
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label="Скачать для Excel"
          className="absolute left-0 z-50 mt-1.5 w-[22rem] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg"
        >
          <div className="space-y-3 p-3">
            <p className="font-onest text-xs leading-4 text-slate-500">
              Скачивается таблица и{" "}
              <span className="font-medium text-slate-700">.chm-sync.json</span>{" "}
              рядом с ней — только форма, колонки и курсор. Вход через{" "}
              id.chm12.ru, секрет в файл не пишется. Обновление допишет только
              новые ответы.
            </p>

            {allowStaffMarks ? (
              <label className="flex items-start gap-2 font-onest text-xs text-slate-700">
                <input
                  type="checkbox"
                  checked={includeStaffMarks}
                  onChange={(event) =>
                    setIncludeStaffMarks(event.target.checked)
                  }
                  className="mt-0.5 h-3.5 w-3.5 rounded border-slate-300 text-brand-500 focus:ring-brand-500/30"
                />
                <span>
                  Включить пометки
                  <span className="mt-0.5 block text-[11px] text-slate-400">
                    По умолчанию в файл не входят
                  </span>
                </span>
              </label>
            ) : null}

            <button
              type="button"
              disabled={busy}
              onClick={() => void handleDownload()}
              className="inline-flex h-8 w-full items-center justify-center gap-1.5 rounded-lg bg-brand-500 px-2.5 font-onest text-xs font-medium text-white hover:bg-brand-600 disabled:opacity-50"
            >
              <IconDownload size={14} />
              {busy ? "Скачивание…" : "Скачать для Excel"}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
